import React, { useEffect, useState, useCallback } from 'react';
import { FiX, FiDownload, FiArrowRight, FiRefreshCw } from 'react-icons/fi';
import {
  generateHandoverSummary,
  downloadHandoverDocx,
  continueFromHandover,
} from '../utils/conversationApi';
import './HandoverModal.css';

const CLOSE_ANIMATION_MS = 220;

function Section({ heading, children }) {
  return (
    <section className="handover-section">
      <h3>{heading}</h3>
      {children}
    </section>
  );
}

function BulletList({ items }) {
  if (!items || items.length === 0) return <p className="handover-muted">None.</p>;
  return (
    <ul>
      {items.map((item, idx) => (
        <li key={idx}>{item}</li>
      ))}
    </ul>
  );
}

export default function HandoverModal({
  isOpen,
  onClose,
  conversationId,
  conversationName,
  onContinued,
}) {
  const [isClosing, setIsClosing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [continuing, setContinuing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  /** Shows the "Plain / Letterhead template" question when the user clicks Download. */
  const [formatPromptOpen, setFormatPromptOpen] = useState(false);
  /** True until the user opts in — opening the modal does not call the API. */
  const [awaitingUserStart, setAwaitingUserStart] = useState(true);

  const loadSummary = useCallback(
    async ({ force = false } = {}) => {
      if (!conversationId) return;
      setError('');
      if (force) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      try {
        const data = await generateHandoverSummary(conversationId, { force });
        setSummary(data);
      } catch (err) {
        setError(err.message || 'Failed to generate handover summary.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [conversationId]
  );

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setSummary(null);
      setError('');
      setLoading(false);
      setRefreshing(false);
      setAwaitingUserStart(true);
      setFormatPromptOpen(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (continuing || downloading) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, CLOSE_ANIMATION_MS);
  };

  const handleDownload = async (format = 'plain') => {
    if (!conversationId) return;
    setDownloading(true);
    setError('');
    try {
      // Capitalized to match the backend's Content-Disposition name, which
      // normally wins -- this hint is only the fallback if that header is absent.
      const prefix = format === 'letterhead' ? 'Letterhead' : 'Handover';
      await downloadHandoverDocx(
        conversationId,
        `${prefix}_${conversationName || 'conversation'}.docx`,
        format
      );
      setFormatPromptOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to download handover document.');
    } finally {
      setDownloading(false);
    }
  };

  const handleContinue = async () => {
    if (!conversationId) return;
    setContinuing(true);
    setError('');
    try {
      const payload = await continueFromHandover(conversationId);
      const newConversation = payload?.conversation;
      if (!newConversation) {
        throw new Error('Backend did not return the new conversation.');
      }
      if (typeof onContinued === 'function') {
        await onContinued(newConversation);
      }
      setIsClosing(true);
      setTimeout(() => onClose(), CLOSE_ANIMATION_MS);
    } catch (err) {
      setError(err.message || 'Failed to start the continued conversation.');
    } finally {
      setContinuing(false);
    }
  };

  const showLoading = loading && !summary;
  const showIntro = awaitingUserStart && !summary && !loading && !error;

  const handleStartSummarizing = () => {
    setAwaitingUserStart(false);
    loadSummary();
  };

  return (
    <div
      className={`handover-modal-overlay ${isClosing ? 'handover-modal-overlay--closing' : ''}`}
      onClick={handleClose}
    >
      <div
        className={`handover-modal ${isClosing ? 'handover-modal--closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="handover-modal-header">
          <div className="handover-modal-header-text">
            <h2>Hand over this conversation</h2>
            <p className="handover-modal-subtitle">
              {conversationName ? `"${conversationName}"` : 'Long conversations get easier to manage with a summary.'}
            </p>
          </div>
          <div className="handover-modal-header-actions">
            {summary && (
              <button
                type="button"
                className="handover-modal-icon-btn"
                onClick={() => loadSummary({ force: true })}
                disabled={loading || refreshing || continuing || downloading}
                title="Regenerate summary"
                aria-label="Regenerate summary"
              >
                <FiRefreshCw size={16} className={refreshing ? 'handover-spin' : ''} />
              </button>
            )}
            <button
              type="button"
              className="handover-modal-icon-btn"
              onClick={handleClose}
              aria-label="Close"
              disabled={continuing || downloading}
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        <div className="handover-modal-body">
          {showIntro ? (
            <div className="handover-intro">
              <p className="handover-intro-lead">
                A <strong>handover</strong> is a written summary of this chat: what you are trying to achieve, what
                was covered, and where things left off. It helps when threads get long or you want a clean slate
                without losing the substance of the work.
              </p>
              <p>You can <strong>download a Word document</strong> to keep or share, and/or{' '}
                <strong>start a new conversation</strong> that is seeded with this summary and links back to this one.
              </p>
              <p className="handover-intro-cost">
                Generating the summary runs an AI on the full thread and can take from a few seconds to a couple of
                minutes for very large chats. Nothing runs until you choose to start.
              </p>
              <div className="handover-intro-action">
                <button
                  type="button"
                  className="handover-btn handover-btn-primary"
                  onClick={handleStartSummarizing}
                  disabled={!conversationId}
                >
                  Start summarizing the conversation
                </button>
              </div>
            </div>
          ) : showLoading ? (
            <div className="handover-loading">
              <div className="handover-spinner" />
              <p>Summarising the conversation in one pass. This can take 30–120 seconds for long or detailed chats.</p>
            </div>
          ) : error && !summary ? (
            <div className="handover-error">
              <p>{error}</p>
              <button type="button" className="handover-btn handover-btn-secondary" onClick={() => loadSummary()}>
                Try again
              </button>
            </div>
          ) : summary ? (
            <div className="handover-preview">
              <div className="handover-preview-title">
                <h3>{summary.title || 'Conversation handover'}</h3>
                {summary.generated_at && (
                  <span className="handover-preview-timestamp">
                    Generated {new Date(summary.generated_at).toLocaleString()}
                  </span>
                )}
              </div>
              {refreshing && <div className="handover-refreshing-note">Regenerating…</div>}

              {summary.user_goal && (
                <Section heading="User goal">
                  <p>{summary.user_goal}</p>
                </Section>
              )}
              {summary.current_state && (
                <Section heading="Current state">
                  <p>{summary.current_state}</p>
                </Section>
              )}
              <Section heading="Topics covered">
                <BulletList items={summary.topics_covered} />
              </Section>
              <Section heading="Key decisions">
                <BulletList items={summary.key_decisions} />
              </Section>
              <Section heading="Artifacts referenced">
                <BulletList items={summary.artifacts_referenced} />
              </Section>
              <Section heading="Open questions">
                <BulletList items={summary.open_questions} />
              </Section>
              <Section heading="Recommended next steps">
                <BulletList items={summary.recommended_next_steps} />
              </Section>

              <p className="handover-fineprint">
                Detail from the original chat is condensed. The original conversation is preserved — the new chat will
                link back to it.
              </p>
              {error && <p className="handover-inline-error">{error}</p>}
            </div>
          ) : null}
        </div>

        <div className="handover-modal-footer">
          {formatPromptOpen ? (
            <div className="handover-format-prompt">
              <span className="handover-format-prompt-label">Which format?</span>
              <button
                type="button"
                className="handover-btn handover-btn-secondary"
                onClick={() => handleDownload('plain')}
                disabled={downloading}
              >
                Plain
              </button>
              <button
                type="button"
                className="handover-btn handover-btn-secondary"
                onClick={() => handleDownload('letterhead')}
                disabled={downloading}
              >
                Letterhead template
              </button>
              <button
                type="button"
                className="handover-modal-icon-btn"
                onClick={() => setFormatPromptOpen(false)}
                disabled={downloading}
                aria-label="Cancel"
                title="Cancel"
              >
                <FiX size={14} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="handover-btn handover-btn-secondary"
              onClick={() => setFormatPromptOpen(true)}
              disabled={!summary || downloading || continuing || loading}
            >
              <FiDownload size={14} style={{ marginRight: 6 }} />
              {downloading ? 'Preparing…' : 'Download .docx'}
            </button>
          )}
          <button
            type="button"
            className="handover-btn handover-btn-primary"
            onClick={handleContinue}
            disabled={!summary || continuing || downloading || loading}
          >
            {continuing ? 'Starting new chat…' : (
              <>
                Start new chat from summary
                <FiArrowRight size={14} style={{ marginLeft: 6 }} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
